import React, { useState, useEffect, useCallback, useRef } from "react";
import { getTimeZoneLabel } from "../../../shared/utils/timeZone";
import { btn } from "../../../shared/styles/buttonStyles";
import axios from "axios";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import formatTimeTo12Hour from "../../../shared/utils/formatTimeTo12Hour";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import ResourceContextPanel from "../../../shared/ui/ResourceContextPanel";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import ConfirmActionDialog from "../../../shared/ui/ConfirmActionDialog";
import { PaginatedResponse } from "../../../shared/types/pagination";
import {
  ActivityFeedEntry,
  ACTIVITY_API_PAGE_SIZE,
  ACTIVITY_LOG_PAGE_SIZE,
  ACTIVITY_LOG_POLL_INTERVAL_MS,
  ExecutionActivityEntry,
  formatActivityValue,
  hasActivityValueChanged,
  isExecutionActive,
  mapExecutionFeedEntries,
  parseActivityDetailsJson,
  sortActivityFeedEntries,
} from "../../../shared/utils/activityFeed";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import { getAccessToken } from "../../../shared/auth/getAccessToken";

const API_URL = process.env.REACT_APP_API_URL;

interface EnvConfig {
  key: "dev" | "prod";
  displayName: string;
  subscriptionId: string;
  resourceGroup: string;
  vmssName: string;
  imageTemplate: string;
}

const envConfigs: EnvConfig[] = [
  {
    key: "dev",
    displayName: "Accelerus Dev",
    subscriptionId: "b5c0aaab-7e02-4f85-be1d-452201e9c3bf",
    resourceGroup: "accelerustest",
    vmssName: "accelerus-dev-worker-vmss",
    imageTemplate: "accelerus-dev-worker-image-template-<version>",
  },
  {
    key: "prod",
    displayName: "Accelerus Prod",
    subscriptionId: "b5c0aaab-7e02-4f85-be1d-452201e9c3bf",
    resourceGroup: "acceleruslive",
    vmssName: "accelerus-prod-worker-vmss",
    imageTemplate: "accelerus-prod-worker-image-template-<version>",
  },
];

interface ScheduleState {
  isEnabled: boolean;
  scheduleTime: string; // "hh:mm tt"
  lastRunAt: string | null;
}

interface ActivityLogEntry {
  id: string;
  actionType: string;
  status: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  durationMs?: number | null;
  changeSummary: string;
  changesJson?: string;
  errorMessage?: string;
  pipelineRunUrl?: string;
}

// ── Environment Card ──────────────────────────────────────────────────────────

interface EnvCardProps {
  env: EnvConfig;
  onRequestRun: (env: EnvConfig) => void;
  running: boolean;
  getToken: () => Promise<string>;
  onScheduleSaved: () => void;
}

const EnvCard = ({ env, onRequestRun, running, getToken, onScheduleSaved }: EnvCardProps) => {
  const [schedule, setSchedule] = useState<ScheduleState>({
    isEnabled: false,
    scheduleTime: "02:00 AM",
    lastRunAt: null,
  });
  const [scheduleInput, setScheduleInput] = useState("02:00"); // 24h for <input type="time">
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [, setDirty] = useState(false);

  const fetchSchedule = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/api/accelerus/maintenance/schedule/${env.key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data as ScheduleState;
      setSchedule(data);
      // Convert "hh:mm tt" to "HH:mm" for input[type=time]
      const [timePart, modifier] = data.scheduleTime.split(" ");
      let [h, m] = timePart.split(":");
      if (modifier === "PM" && h !== "12") h = String(parseInt(h) + 12).padStart(2, "0");
      if (modifier === "AM" && h === "12") h = "00";
      setScheduleInput(`${h}:${m}`);
      setDirty(false);
    } catch {
      // default already set
    }
  }, [env.key, getToken]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const handleToggleEnabled = (checked: boolean) => {
    setSchedule((s) => ({ ...s, isEnabled: checked }));
    setDirty(true);
  };

  const handleTimeChange = (val: string) => {
    setScheduleInput(val);
    setDirty(true);
  };

  const handleSaveSchedule = async () => {
    try {
      setSavingSchedule(true);
      const token = await getToken();
      const scheduleTime12h = formatTimeTo12Hour(scheduleInput);
      await axios.put(
        `${API_URL}/api/accelerus/maintenance/schedule/${env.key}`,
        { isEnabled: schedule.isEnabled, scheduleTime: scheduleTime12h },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchSchedule();
      setDirty(false);
      toast.success(`Schedule applied for ${env.displayName}.`);
      onScheduleSaved();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to apply schedule."));
    } finally {
      setSavingSchedule(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <ResourceContextPanel
        resourceName={env.vmssName}
        portalUrl={`https://portal.azure.com/#resource/subscriptions/${encodeURIComponent(env.subscriptionId)}/resourceGroups/${encodeURIComponent(env.resourceGroup)}/overview`}
      />
      <section className="workspace-card flex flex-col overflow-hidden border border-slate-200 bg-white">
        <div className="order-2 border-t border-slate-200 px-5 py-4">
          <h3 className="text-sm font-bold leading-5 text-slate-800">Manual Action</h3>
          <p className="mt-1 text-[11px] leading-4 text-slate-500">Run cleanup immediately.</p>
          <hr className="my-3 border-slate-200" />
          <div className="flex justify-start">
            <button
              type="button"
              disabled={running}
              onClick={() => onRequestRun(env)}
              className={btn.destructiveSmall}
            >
              {running ? "Running..." : "Run Cleanup"}
            </button>
          </div>
        </div>

        <div className="order-1 px-5 py-4">
          <h3 className="text-sm font-bold leading-5 text-slate-800">Schedule Automation</h3>
          <p className="mt-1 text-[11px] leading-4 text-slate-500">Run cleanup daily in {getTimeZoneLabel()}.</p>
          <hr className="my-3 border-slate-200" />
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs">
              <label htmlFor="worker-maintenance-schedule-enabled" className="font-semibold text-slate-700">
                Enable schedule
              </label>
              <input
                id="worker-maintenance-schedule-enabled"
                type="checkbox"
                checked={schedule.isEnabled}
                onChange={(e) => handleToggleEnabled(e.target.checked)}
                className="h-4 w-4 cursor-pointer rounded border-slate-300"
              />
            </div>
            <div className="rounded-sm border border-slate-200 bg-white px-3 py-2.5 text-xs">
              <div className="grid grid-cols-[72px,minmax(0,144px),auto] items-center gap-3">
                <label htmlFor="worker-maintenance-schedule-time" className="font-medium text-slate-600">
                  Run at
                </label>
                <input
                  id="worker-maintenance-schedule-time"
                  type="time"
                  value={scheduleInput}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  disabled={!schedule.isEnabled}
                  className="w-full max-w-[144px]"
                />
                <span className="text-slate-400">{getTimeZoneLabel()}</span>
              </div>
            </div>
          </div>
          <div className="mt-3 flex justify-start">
            <button
              type="button"
              disabled={savingSchedule}
              onClick={handleSaveSchedule}
              className={btn.applySmall}
            >
              {savingSchedule ? "Applying..." : "Apply Schedule"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface WorkerMaintenanceProps {
  env: "dev" | "prod";
}

const WorkerMaintenance = ({ env }: WorkerMaintenanceProps) => {
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");
  const { instance, accounts } = useMsal();
  const [runningEnv, setRunningEnv] = useState<EnvConfig["key"] | null>(null);
  const [pendingRun, setPendingRun] = useState<EnvConfig | null>(null);
  const [activityFeedEntries, setActivityFeedEntries] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState(false);
  const filteredEnvConfigs = envConfigs.filter((e) => e.key === env);
  // Dev and Prod routes share this instance, fetches check this to drop late responses for the old env
  const selectedEnvRef = useRef(env);
  selectedEnvRef.current = env;


  // Audit rows store each field as { before, after }
  const extractAuditChangeLines = (changesJson?: string) => {
    const parsed = parseActivityDetailsJson<Record<string, { before?: unknown; after?: unknown }>>(changesJson);
    const labels: Record<string, string> = {
      isEnabled: "Schedule Enabled",
      scheduleTime: "Schedule Time",
    };

    return Object.entries(labels)
      .filter(([key]) => parsed?.[key] && hasActivityValueChanged(parsed[key].before, parsed[key].after))
      .map(([key, label]) => `${label}: ${formatActivityValue(parsed?.[key].before)} -> ${formatActivityValue(parsed?.[key].after)}`);
  };

  const getActivityChangeLines = (log: ActivityFeedEntry) => {
    if (log.source === "execution") {
      return [];
    }

    return extractAuditChangeLines(log.detailsJson);
  };

  const buildActivityFeed = (
    executionItems: ExecutionActivityEntry[],
    auditItems: ActivityLogEntry[]
  ): ActivityFeedEntry[] => {
    const executionFeed = mapExecutionFeedEntries(executionItems);

    const auditFeed: ActivityFeedEntry[] = auditItems
      .filter((log) => log.actionType === "save_schedule")
      .map((log) => ({
        id: log.id,
        source: "audit" as const,
        actionType: log.actionType,
        status: log.status,
        actorName: log.actorName,
        actorEmail: log.actorEmail,
        startedAtUtc: log.startedAtUtc,
        updatedAtUtc: undefined,
        durationMs: log.durationMs,
        detailsJson: log.changesJson,
        errorMessage: log.errorMessage,
        pipelineRunUrl: undefined,
      }));

    return sortActivityFeedEntries([...executionFeed, ...auditFeed]);
  };

  const fetchActivityLogs = useCallback(async (page = 1, showErrorToast = true, showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setActivityLogsLoading(true);
      }
      const token = await getAccessToken();
      const [auditItems, executionItems] = await Promise.all([
        axios.get<PaginatedResponse<ActivityLogEntry>>(`${API_URL}/api/accelerus/maintenance/activity-logs/${env}`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${token}` },
        }).then((response) => response.data.items),
        axios.get<PaginatedResponse<ExecutionActivityEntry>>(`${API_URL}/api/accelerus/maintenance/executions/${env}`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${token}` },
        }).then((response) => response.data.items),
      ]);
      if (env !== selectedEnvRef.current) return;
      const mergedFeed = buildActivityFeed(executionItems, auditItems);
      const totalCount = mergedFeed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);
      const pagedItems = mergedFeed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE,
      );

      setActivityFeedEntries(pagedItems);
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      setHasActiveExecutions(executionItems.some((item) => isExecutionActive(item.status)));
    } catch {
      // Failed poll keeps the last rows and keeps polling
      if (!showLoadingState || env !== selectedEnvRef.current) {
        return;
      }
      if (showErrorToast) {
        toast.error("Failed to load activity logs.");
      }
      setActivityFeedEntries([]);
      setActivityLogsPage(1);
      setActivityLogsTotalPages(1);
      setActivityLogsTotalCount(0);
      setHasActiveExecutions(false);
    } finally {
      if (showLoadingState) {
        setActivityLogsLoading(false);
      }
    }
  }, [env]);

  const handleRequestRun = (env: EnvConfig) => setPendingRun(env);

  const handleConfirm = async () => {
    if (!pendingRun) return;
    const env = pendingRun;
    setPendingRun(null);

    try {
      setRunningEnv(env.key);
      const token = await getAccessToken();
      const response = await axios.post(
        `${API_URL}/api/accelerus/vmss-images/run`,
        { env: env.key, dryRun: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data?.message ?? `${env.displayName} cleanup pipeline has been queued for execution.`);
      setHasActiveExecutions(true);
      void fetchActivityLogs(1, false, false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to trigger pipeline."));
    } finally {
      setRunningEnv(null);
    }
  };

  useEffect(() => {
    if (instance && accounts.length > 0) {
      fetchActivityLogs(1);
    }
  }, [instance, accounts, fetchActivityLogs]);

  useEffect(() => {
    if (!hasActiveExecutions) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchActivityLogs(activityLogsPage, false, false);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activityLogsPage, fetchActivityLogs, hasActiveExecutions]);

  const activityLogRows: ActivityLogTableItem[] = activityFeedEntries.map((log) => ({
    id: log.id,
    dateText: formatAuditDate(log.startedAtUtc),
    timeText: formatAuditTime24Hour(log.startedAtUtc),
    durationText: formatActivityDuration(log.durationMs),
    runningAtUtc: log.runningAtUtc,
    actionText: log.actionType === "run_cleanup" ? "Run Cleanup" : "Apply Schedule",
    byName: log.actorName || "Unknown",
    byEmail: log.actorEmail,
    status: log.status,
    changeLines: getActivityChangeLines(log),
    errorMessage: log.errorMessage,
    pipelineRunUrl: log.pipelineRunUrl,
  }));

  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Removes old Accelerus worker image templates and versions without changing running Worker Role (VMSS) instances.">
          <h3 className="text-sm font-semibold text-slate-800">Cleanup scope</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Run cleanup now or set a daily schedule.</li>
            <li>Cleanup keeps the latest image template and version.</li>
            <li>At least one template and one version remain.</li>
            <li>Running Worker Role (VMSS) instances are not changed.</li>
            <li>Execution: Azure DevOps pipeline.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {/* Hidden instead of unmounted so EnvCard keeps unsaved schedule edits and doesn't refetch on tab switch */}
      <div id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0} hidden={activeTab !== "operations"}>
      <div className="mt-2 space-y-2">
        {filteredEnvConfigs.map((e) => (
          <EnvCard
            key={e.key}
            env={e}
            onRequestRun={handleRequestRun}
            running={runningEnv === e.key}
            getToken={getAccessToken}
            onScheduleSaved={() => fetchActivityLogs(1, false, false)}
          />
        ))}
      </div>

      <ActivityLogTable
        title="Activity Log"
        description="Tracks cleanup runs and schedule changes."
        items={activityLogRows}
        loading={activityLogsLoading}
        page={activityLogsPage}
        totalPages={activityLogsTotalPages}
        totalCount={activityLogsTotalCount}
        onRefresh={() => fetchActivityLogs(activityLogsPage)}
        onPageChange={(pageNumber) => fetchActivityLogs(pageNumber)}
      />
      </div>

      <ConfirmActionDialog
        open={!!pendingRun}
        title={`Confirm Cleanup — ${pendingRun?.displayName ?? ""}`}
        description={`This will permanently delete all image templates and image versions for ${pendingRun?.displayName ?? ""}, except the latest one. This action cannot be undone.`}
        confirmLabel="Yes, delete resources"
        destructive
        onConfirm={handleConfirm}
        onCancel={() => setPendingRun(null)}
      >
        <div className="space-y-3 text-xs text-slate-700">
          <div className="rounded-sm bg-slate-50 border border-slate-200 p-3 space-y-1">
            <div><span className="font-medium text-slate-500">Resource Group: </span>{pendingRun?.resourceGroup}</div>
            <div><span className="font-medium text-slate-500">Worker Role (VMSS): </span>{pendingRun?.vmssName}</div>
            <div><span className="font-medium text-slate-500">Image Template: </span>{pendingRun?.imageTemplate}</div>
          </div>
        </div>
      </ConfirmActionDialog>
    </div>
  );
};

export default WorkerMaintenance;
